import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  RotateCcw,
  Loader2,
  Mail,
  ArrowUpRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 10 questions, each option has a score 0-10
const QUESTIONS = [
  {
    id: "q1",
    label: "Avez-vous une politique de sécurité formellement documentée et validée par la direction ?",
    options: [
      { label: "Aucune politique", score: 0 },
      { label: "Brouillon non validé", score: 3 },
      { label: "Documentée mais non communiquée", score: 6 },
      { label: "Documentée, communiquée et auditée annuellement", score: 10 },
    ],
  },
  {
    id: "q2",
    label: "Réalisez-vous une analyse de risques (EBIOS RM / ISO 27005) ?",
    options: [
      { label: "Jamais", score: 0 },
      { label: "De manière informelle", score: 3 },
      { label: "Tous les 2-3 ans", score: 7 },
      { label: "Annuellement, avec revue", score: 10 },
    ],
  },
  {
    id: "q3",
    label: "Êtes-vous certifié ISO 27001 ou engagé dans une démarche de certification ?",
    options: [
      { label: "Non, pas d'intérêt", score: 0 },
      { label: "Intéressé, pas commencé", score: 4 },
      { label: "Démarche en cours", score: 7 },
      { label: "Certifié et maintenu", score: 10 },
    ],
  },
  {
    id: "q4",
    label: "Disposez-vous d'un SOC / SIEM pour surveiller vos systèmes 24/7 ?",
    options: [
      { label: "Pas de surveillance", score: 0 },
      { label: "Logs collectés mais non exploités", score: 3 },
      { label: "SIEM ou EDR partiel", score: 7 },
      { label: "SOC 24/7 (interne ou managé)", score: 10 },
    ],
  },
  {
    id: "q5",
    label: "Comment gérez-vous les accès à privilèges (admin, root, DB) ?",
    options: [
      { label: "Comptes partagés sans contrôle", score: 0 },
      { label: "Comptes nominatifs sans rotation", score: 3 },
      { label: "MFA + rotation manuelle", score: 7 },
      { label: "Solution PAM avec vault & audit", score: 10 },
    ],
  },
  {
    id: "q6",
    label: "Vos collaborateurs sont-ils sensibilisés à la cybersécurité ?",
    options: [
      { label: "Aucune sensibilisation", score: 0 },
      { label: "Sensibilisation ponctuelle", score: 4 },
      { label: "Formation annuelle obligatoire", score: 7 },
      { label: "Programme continu + phishing tests", score: 10 },
    ],
  },
  {
    id: "q7",
    label: "Avez-vous un plan de réponse à incident testé ?",
    options: [
      { label: "Aucun plan", score: 0 },
      { label: "Plan écrit, jamais testé", score: 3 },
      { label: "Plan testé sur table", score: 7 },
      { label: "Exercices red team / cyber crise réguliers", score: 10 },
    ],
  },
  {
    id: "q8",
    label: "Êtes-vous conforme RGPD (DPO, registre, DPIA) ?",
    options: [
      { label: "Pas conforme", score: 0 },
      { label: "Démarche entamée", score: 4 },
      { label: "Registre + DPIA à jour", score: 7 },
      { label: "Programme RGPD complet, audité", score: 10 },
    ],
  },
  {
    id: "q9",
    label: "Si NIS 2 vous concerne, êtes-vous prêt pour l'échéance ?",
    options: [
      { label: "Non concerné / je ne sais pas", score: 5 },
      { label: "Concerné, pas commencé", score: 0 },
      { label: "Diagnostic réalisé, plan en cours", score: 7 },
      { label: "Conformité atteinte et documentée", score: 10 },
    ],
  },
  {
    id: "q10",
    label: "Réalisez-vous des tests d'intrusion (pentest) ?",
    options: [
      { label: "Jamais", score: 0 },
      { label: "Une fois, il y a >2 ans", score: 3 },
      { label: "Annuellement sur le périmètre clé", score: 7 },
      { label: "Pentest + bug bounty + red team continus", score: 10 },
    ],
  },
];

function scoreToLevel(score) {
  if (score >= 85) return { level: "Excellence", color: "emerald", desc: "Posture de référence — vous êtes parmi les organisations les plus matures. Maintenir l'excellence et anticiper les menaces émergentes." };
  if (score >= 65) return { level: "Robuste", color: "emerald", desc: "Posture solide. Quelques optimisations ciblées vous porteraient au niveau Excellence (certification, threat intel, automatisation SOC)." };
  if (score >= 40) return { level: "Établi", color: "yellow", desc: "Les bases sont là, mais des écarts critiques persistent. Un programme structuré sur 9-12 mois vous transformerait significativement." };
  return { level: "Émergent", color: "red", desc: "Risque élevé. Une remise à plat de votre gouvernance et de vos défenses opérationnelles est prioritaire — c'est notre spécialité." };
}

export default function MaturityQuiz() {
  const [step, setStep] = useState(0); // 0 = intro, 1-10 = questions, 11 = email, 12 = result
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ email: "", name: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const totalQuestions = QUESTIONS.length;
  const progress = step === 0 ? 0 : step <= totalQuestions ? (step / totalQuestions) * 100 : 100;

  const handleAnswer = (qid, optionIndex) => {
    setAnswers({ ...answers, [qid]: optionIndex });
    setTimeout(() => setStep(step + 1), 240);
  };

  const computeScore = () => {
    const raw = QUESTIONS.reduce((sum, q) => {
      const i = answers[q.id];
      if (i === undefined) return sum;
      return sum + q.options[i].score;
    }, 0);
    return Math.round((raw / (QUESTIONS.length * 10)) * 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.email) {
      toast.error("Veuillez renseigner votre email pour recevoir le rapport.");
      return;
    }
    setLoading(true);
    const score = computeScore();
    const levelInfo = scoreToLevel(score);
    const answersPayload = QUESTIONS.map((q) => ({
      question_id: q.id,
      value: answers[q.id] ?? 0,
    }));
    try {
      await axios.post(`${API}/quiz/submit`, {
        email: contact.email,
        name: contact.name || null,
        company: contact.company || null,
        answers: answersPayload,
        score,
        level: levelInfo.level,
      });
      setResult({ score, ...levelInfo });
      setStep(12);
      toast.success("Votre rapport personnalisé arrive dans votre boîte mail.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setContact({ email: "", name: "", company: "" });
    setResult(null);
  };

  const isQuestion = step >= 1 && step <= totalQuestions;
  const currentQ = isQuestion ? QUESTIONS[step - 1] : null;

  return (
    <section
      id="quiz"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="aurora" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="text-center mb-12">
          <div className="font-mono text-xs text-emerald-400 mb-4 tracking-widest">
            // ÉVALUATION GRATUITE · 60 SECONDES
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
            Quel est votre niveau de
            <br />
            <span className="italic text-emerald-400">maturité cyber</span> ?
          </h2>
          <p className="mt-6 text-gray-400 max-w-2xl mx-auto">
            10 questions. Un score sur 100. Un rapport personnalisé envoyé par email avec
            recommandations priorisées par nos experts.
          </p>
        </div>

        {/* Progress bar */}
        {step > 0 && step < 12 && (
          <div className="mb-8 max-w-3xl mx-auto">
            <div className="flex justify-between text-[11px] font-mono text-gray-500 mb-2">
              <span data-testid="quiz-progress-label">
                {step <= totalQuestions
                  ? `QUESTION ${step}/${totalQuestions}`
                  : `RÉSULTATS`}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#0C1410] border border-[#1A2E22] overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </div>
        )}

        <div
          data-testid="maturity-quiz"
          className="relative rounded-3xl bg-[#0C1410] border border-[#1A2E22] p-7 md:p-12 max-w-3xl mx-auto min-h-[420px] flex flex-col"
        >
          <AnimatePresence mode="wait">
            {/* Intro */}
            {step === 0 && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="flex-1 flex flex-col items-center justify-center text-center"
              >
                <div className="inline-flex p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mb-6">
                  <ShieldCheck className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold mb-3">
                  Évaluez votre posture en 60 secondes
                </h3>
                <p className="text-gray-400 max-w-md mb-8">
                  Gouvernance, défense, conformité — 10 questions couvrant les 4 piliers
                  de la cybersécurité moderne.
                </p>
                <button
                  onClick={() => setStep(1)}
                  data-testid="quiz-start-button"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-all hover:-translate-y-[2px] glow-emerald"
                >
                  Démarrer le diagnostic
                  <ArrowRight className="w-4 h-4" />
                </button>
                <div className="mt-6 flex items-center gap-4 text-[11px] font-mono text-gray-500">
                  <span>✓ Anonyme</span>
                  <span>✓ Sans engagement</span>
                  <span>✓ Rapport PDF offert</span>
                </div>
              </motion.div>
            )}

            {/* Question */}
            {isQuestion && (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="flex-1"
              >
                <h3 className="font-display text-xl md:text-2xl font-medium leading-snug mb-8">
                  {currentQ.label}
                </h3>
                <div className="space-y-3">
                  {currentQ.options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => handleAnswer(currentQ.id, i)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className="w-full text-left px-5 py-4 rounded-xl border border-[#1A2E22] hover:border-emerald-500/60 hover:bg-emerald-500/5 transition-all flex items-center gap-4 group"
                    >
                      <span className="font-mono text-xs text-emerald-400 w-6">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="flex-1 text-white">{opt.label}</span>
                      <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                    </button>
                  ))}
                </div>
                {step > 1 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    data-testid="quiz-back-button"
                    className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-400 transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3" /> Question précédente
                  </button>
                )}
              </motion.div>
            )}

            {/* Email gate */}
            {step === totalQuestions + 1 && (
              <motion.form
                key="email-gate"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                onSubmit={handleSubmit}
                className="flex-1 flex flex-col"
              >
                <div className="inline-flex p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mb-5 self-start">
                  <Sparkles className="w-5 h-5" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold mb-3">
                  Votre rapport est prêt.
                </h3>
                <p className="text-gray-400 mb-7">
                  Renseignez votre email pour recevoir votre score détaillé,
                  benchmark sectoriel et recommandations priorisées (PDF).
                </p>
                <div className="space-y-3 mb-6">
                  <input
                    type="email"
                    required
                    placeholder="email professionnel *"
                    value={contact.email}
                    onChange={(e) =>
                      setContact({ ...contact, email: e.target.value })
                    }
                    data-testid="quiz-email-input"
                    className="w-full bg-[#080F0B] border border-[#1A2E22] focus:border-emerald-500/60 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none transition-colors"
                  />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="prénom"
                      value={contact.name}
                      onChange={(e) =>
                        setContact({ ...contact, name: e.target.value })
                      }
                      data-testid="quiz-name-input"
                      className="w-full bg-[#080F0B] border border-[#1A2E22] focus:border-emerald-500/60 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none transition-colors"
                    />
                    <input
                      type="text"
                      placeholder="entreprise"
                      value={contact.company}
                      onChange={(e) =>
                        setContact({ ...contact, company: e.target.value })
                      }
                      data-testid="quiz-company-input"
                      className="w-full bg-[#080F0B] border border-[#1A2E22] focus:border-emerald-500/60 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none transition-colors"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quiz-submit-button"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-all disabled:opacity-50 glow-emerald"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Recevoir mon rapport <Mail className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-[11px] text-gray-500 mt-4">
                  Nous ne partageons jamais votre email. Désinscription en 1 clic.
                </p>
              </motion.form>
            )}

            {/* Result */}
            {step === 12 && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                className="flex-1 flex flex-col items-center text-center"
              >
                <div className="inline-flex p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mb-5">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <div className="font-mono text-[11px] text-emerald-400 mb-3 tracking-widest">
                  // VOTRE SCORE MATURITÉ
                </div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span
                    data-testid="quiz-result-score"
                    className="font-display text-7xl md:text-8xl font-bold text-emerald-400 leading-none"
                  >
                    {result.score}
                  </span>
                  <span className="font-display text-3xl text-gray-500">/100</span>
                </div>
                <div
                  data-testid="quiz-result-level"
                  className="text-2xl md:text-3xl font-display font-medium mb-4"
                >
                  Niveau : <span className="italic text-emerald-400">{result.level}</span>
                </div>
                <p className="text-gray-400 max-w-md mb-7 leading-relaxed">
                  {result.desc}
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3 mb-4">
                  <a
                    href="#contact"
                    data-testid="quiz-result-cta"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-all hover:-translate-y-[2px]"
                  >
                    Discuter de mon score
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                  <button
                    onClick={restart}
                    data-testid="quiz-restart-button"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 hover:border-emerald-500/50 text-gray-300 hover:text-emerald-400 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" /> Refaire le diagnostic
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  Rapport détaillé envoyé à <span className="text-gray-300">{contact.email}</span>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
